import React, { useEffect, useMemo, useState } from 'react';
import { Container, Paper, Box, Typography, TextField, MenuItem, Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, InputAdornment, Autocomplete, Alert } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import Menu from '../components/Menu';
import BillingPowerCard from '../components/BillingPowerCard';
import api from '../utils/apiClient';
import useRefetchOnHostingChange from '../hooks/useRefetchOnHostingChange';
import { useNotifications } from '../context/NotificationContext';
import { getLocale } from '../utils/dateLocale';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { normalizeRole } from '../utils/roles';
import { API_BASE as BILL_API_BASE_URL } from '../utils/apiConfig';
import { fetchAllBills } from '../utils/fetchAllBills';
import { resolveBillName } from '../utils/billName';
import { getBillSourceCaption } from '../utils/billSource';
import { formatBillAmount, formatRubAmount } from '../utils/billAmount';
import { localizeItemName } from '../utils/billItemName';
import { getBillItemDisplay, getBillItemsTotal, isCustomBillType } from '../utils/billItemDisplay';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const sampleBills = [
  { id: '1', user: 'user1@example.com', title: 'Счет за февраль', status: 'Выставлен', amount: 29700, issuedAt: '01.02.2025' },
  { id: '2', user: 'user2@example.com', title: 'Счет за январь', status: 'Оплачено', amount: 12000, issuedAt: '01.01.2025' },
  { id: '3', user: 'user3@example.com', title: 'Счет за март', status: 'Просрочен', amount: 18000, issuedAt: '01.03.2025' },
];

const statusColor = (rawStatus) => {
  switch (rawStatus) {
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'overdue':
      return 'error';
    default:
      return 'default';
  }
};

const Payments = () => {
  const { t } = useTranslation('billing');
  const { showError } = useNotifications();
  const { user } = useAuth();
  const currentRole = normalizeRole(user?.role);
  const isHoster = currentRole === 'hoster';
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Выгрузка часов работы асиков: по умолчанию прошлый месяц
  const [workMonth, setWorkMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [workExporting, setWorkExporting] = useState(false);

  const handleWorkHoursExport = async () => {
    if (!/^\d{4}-\d{2}$/.test(workMonth)) return;
    try {
      setWorkExporting(true);
      const res = await api.get(`${BILL_API_BASE_URL}work_hours/export`, { params: { month: workMonth }, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `work_hours_${workMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showError(t('workHoursExportError'));
    } finally {
      setWorkExporting(false);
    }
  };
  const [createOpen, setCreateOpen] = useState(false);
  const [bills, setBills] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(0); // 0-11
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [hoursInMonth, setHoursInMonth] = useState(744);
  const [deviceRows, setDeviceRows] = useState([]);
  const [invoiceTitle, setInvoiceTitle] = useState('');
  // kwh — размещение по кВт·ч (часы хеширования × мощность × тариф, считает сервер);
  // monitoring — прежний счёт по договору (только администратор); custom — произвольные позиции
  const [billMode, setBillMode] = useState(isHoster ? 'kwh' : 'monitoring');
  const [kwhPreview, setKwhPreview] = useState(null);
  const [kwhLoading, setKwhLoading] = useState(false);
  const [kwhError, setKwhError] = useState('');
  const [customRows, setCustomRows] = useState([{ name: '', amount: 0 }]);
  const [clientReqOptions, setClientReqOptions] = useState([]);
  const [clientReqLoading, setClientReqLoading] = useState(false);
  const [selectedClientReqId, setSelectedClientReqId] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [viewBill, setViewBill] = useState(null);
  // Счёт по кВт·ч у хостера имеет тип service, а позиции у него те же: асик, часы, кВт·ч
  const viewBillType = viewBill?.content?.method === 'kwh' ? 'monitoring' : viewBill?.type;
  const [viewPayments, setViewPayments] = useState([]);
  const navigate = useNavigate();

  const toRuPaymentStatus = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'pending' || v.includes('check')) return t('paymentStatusPending');
    if (v === 'paid' || v === 'success' || v === 'completed') return t('paymentStatusSuccess');
    if (v === 'failed' || v === 'rejected' || v === 'declined') return t('paymentStatusFailed');
    return s || '';
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      const date = d.toLocaleDateString(getLocale());
      const time = d.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
      return `${date} ${time}`;
    } catch (_) { return String(iso); }
  };

  const toRuStatus = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'pending') return t('statusPending');
    if (v === 'paid' || v === 'success' || v === 'completed') return t('statusPaid');
    if (v === 'overdue' || v === 'expired') return t('statusOverdue');
    if (v.includes('check')) return t('statusChecking');
    return s || '—';
  };

  const getRawStatus = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'pending') return 'pending';
    if (v === 'paid' || v === 'success' || v === 'completed') return 'paid';
    if (v === 'overdue' || v === 'expired') return 'overdue';
    if (v.includes('check')) return 'checking';
    return v;
  };

  const fetchUsers = async () => {
    try {
      if (isHoster) {
        const res = await api.get(`${BILL_API_BASE_URL}me/clients`);
        const list = Array.isArray(res?.data?.clients) ? res.data.clients : [];
        setUsers(list.map(c => ({ id: c.id, name: c.name || null, email: c.email || null })));
      } else {
        const res = await api.get(`${API_BASE_URL}users`);
        setUsers(Array.isArray(res?.data?.users) ? res.data.users : []);
      }
    } catch (e) {
      console.error('fetchUsers error', e);
      setUsers([]);
    }
  };

  const monthNames = [t('monthJanuary'),t('monthFebruary'),t('monthMarch'),t('monthApril'),t('monthMay'),t('monthJune'),t('monthJuly'),t('monthAugust'),t('monthSeptember'),t('monthOctober'),t('monthNovember'),t('monthDecember')];

  const formatDateYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const fetchClientRequisites = async (userId) => {
    if (!userId) {
      setClientReqOptions([]);
      setSelectedClientReqId('');
      return;
    }
    try {
      setClientReqLoading(true);
      const res = await api.get(`${BILL_API_BASE_URL}client-requisites/${userId}`);
      const list = Array.isArray(res?.data?.requisites) ? res.data.requisites : [];
      setClientReqOptions(list);
      const def = list.find((r) => r.is_default);
      setSelectedClientReqId(def ? def.id : (list[0]?.id || ''));
    } catch (e) {
      setClientReqOptions([]);
      setSelectedClientReqId('');
    } finally {
      setClientReqLoading(false);
    }
  };

  const fetchPaymentsPreview = async (userId, year, monthIdx) => {
    if (!userId || !Number.isInteger(year) || !Number.isInteger(monthIdx)) return;
    try {
      const start = new Date(year, monthIdx, 1);
      const end = new Date(year, monthIdx + 1, 0);
      const params = {
        user_id: userId,
        start_date: formatDateYMD(start),
        end_date: formatDateYMD(end)
      };
      const res = await api.get(`${BILL_API_BASE_URL}payments_preview`, { params });
      const devices = Array.isArray(res?.data?.devices) ? res.data.devices : [];
      const hm = calcHoursInMonth(year, monthIdx);
      setHoursInMonth(hm);
      setDeviceRows(devices.map((d, idx) => ({
        device: d.device_name || `Устройство${idx + 1}`,
        sn: d.serial_number || '',
        activeHours: Number(d.working_hours) || 0,
        monthlyTariff: Math.floor(Number(d.contract_amount) || 0),
        hourlyRate: ceilTo2((Number(d.contract_amount) || 0) / hm),
        customTotal: null,
        worker: d.worker || ''
      })));
      if (!invoiceTitle) {
        setInvoiceTitle(t('equipmentInvoiceFor', { month: monthNames[monthIdx] }));
      }
    } catch (e) {
      console.error('fetchPaymentsPreview error', e);
    }
  };

  const ceilTo2 = (value) => {
    const n = Number(value || 0);
    return Math.ceil(n * 100) / 100;
  };

  const parseNumberInput = (value) => {
    const normalized = String(value ?? '')
      .replace(',', '.')
      .replace(/\s+/g, '');
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const calcHoursInMonth = (year, monthIdx) => {
    const days = new Date(year, monthIdx + 1, 0).getDate();
    return days * 24;
  };

  const initPreviousMonth = () => {
    const now = new Date();
    let month = now.getMonth() - 1;
    let year = now.getFullYear();
    if (month < 0) { month = 11; year -= 1; }
    setSelectedMonth(month);
    setSelectedYear(year);
    const hm = calcHoursInMonth(year, month);
    setHoursInMonth(hm);
    setInvoiceTitle(t('equipmentInvoiceFor', { month: monthNames[month] }));
    setDeviceRows([
      { device: 'Устройство1', sn: '', activeHours: 744, monthlyTariff: 10000, hourlyRate: ceilTo2(10000 / hm), customTotal: null },
      { device: 'Устройство2', sn: '', activeHours: 740, monthlyTariff: 11000, hourlyRate: ceilTo2(11000 / hm), customTotal: null },
      { device: 'Устройство3', sn: '', activeHours: 733, monthlyTariff: 9900, hourlyRate: ceilTo2(9900 / hm), customTotal: null },
    ]);
  };

  useEffect(() => {
    if (createOpen) {
      initPreviousMonth();
      setSelectedUser('');
      setClientReqOptions([]);
      setSelectedClientReqId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen]);

  const formatClientReqOption = (item) => {
    if (!item) return '';
    const typeLabel = item.type === 'legal' ? t('billClientReqTypeLegal')
      : item.type === 'ip' ? t('billClientReqTypeIp')
      : t('billClientReqTypeIndividual');
    const head = item.label
      || (item.type === 'legal' ? item.company_name : item.full_name)
      || typeLabel;
    const innPart = item.inn ? ` · ${item.inn}` : '';
    const defaultMark = item.is_default ? ` (${t('billClientRequisitesDefault')})` : '';
    return `${head}${innPart}${defaultMark}`;
  };

  // Расчёт счёта по кВт·ч для выбранного клиента и месяца — с сервера
  useEffect(() => {
    if (!createOpen || billMode !== 'kwh' || !selectedUser) { setKwhPreview(null); setKwhError(''); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        setKwhLoading(true); setKwhError('');
        const month = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
        const res = await api.get(`${BILL_API_BASE_URL}hosting_bill/preview`, { params: { user_id: selectedUser, month } });
        if (!cancelled) setKwhPreview(res.data || null);
      } catch (e) {
        if (!cancelled) { setKwhPreview(null); setKwhError(e?.response?.data?.error || t('kwhPreviewError')); }
      } finally {
        if (!cancelled) setKwhLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen, billMode, selectedUser, selectedYear, selectedMonth]);

  const handleMonthOrYearChange = (nextYear, nextMonth) => {
    const hm = calcHoursInMonth(nextYear, nextMonth);
    setHoursInMonth(hm);
    setDeviceRows(prev => prev.map(r => ({
      ...r,
      hourlyRate: ceilTo2((Number(r.monthlyTariff) || 0) / hm)
    })));
    if (selectedUser) {
      void fetchPaymentsPreview(selectedUser, nextYear, nextMonth);
    }
  };

  const handleChangeMonthlyTariff = (index, value) => {
    const monthlyTariff = parseNumberInput(value);
    const monthlyTariffInt = Math.floor(monthlyTariff);
    setDeviceRows(prev => prev.map((r, i) => i === index ? {
      ...r,
      monthlyTariff: monthlyTariffInt,
      hourlyRate: ceilTo2(monthlyTariffInt / hoursInMonth)
    } : r));
  };

  const handleChangeHourlyRate = (index, value) => {
    const hourlyRate = parseNumberInput(value);
    setDeviceRows(prev => prev.map((r, i) => i === index ? {
      ...r,
      hourlyRate,
      monthlyTariff: Math.floor(hourlyRate * hoursInMonth) // тариф без копеек
    } : r));
  };

  const handleChangeActiveHours = (index, value) => {
    const activeHours = Number(value || 0);
    setDeviceRows(prev => prev.map((r, i) => i === index ? { ...r, activeHours } : r));
  };

  const rowTotal = (r) => {
    const calculated = ceilTo2(r.hourlyRate) * (Number(r.activeHours) || 0);
    const monthlyCap = Number(r.monthlyTariff) || 0;
    const base = (r.customTotal != null && !Number.isNaN(r.customTotal)) ? r.customTotal : calculated;
    const capped = Math.min(base, monthlyCap);
    return Math.ceil(capped);
  };
  const grandTotal = useMemo(() => deviceRows.reduce((acc, r) => acc + rowTotal(r), 0), [deviceRows]);

  const customItemsValid = useMemo(() => {
    if (billMode !== 'custom') return true;
    return customRows.some((r) => r && String(r.name || '').trim().length > 0 && Number(r.amount) > 0);
  }, [billMode, customRows]);

  const handleChangeDevice = (index, value) => {
    setDeviceRows(prev => prev.map((r, i) => i === index ? { ...r, device: value } : r));
  };

  const handleChangeRowTotal = (index, value) => {
    if (value === '') {
      setDeviceRows(prev => prev.map((r, i) => i === index ? { ...r, customTotal: null } : r));
      return;
    }
    const parsed = parseNumberInput(value);
    setDeviceRows(prev => prev.map((r, i) => i === index ? { ...r, customTotal: parsed } : r));
  };

  const handleChangeSN = (index, value) => {
    setDeviceRows(prev => prev.map((r, i) => i === index ? { ...r, sn: value } : r));
  };

  

  const handleAddRow = () => {
    const nextIdx = deviceRows.length + 1;
    setDeviceRows(prev => ([
      ...prev,
      {
        device: `Устройство${nextIdx}`,
        sn: '',
        activeHours: 744,
        monthlyTariff: 0,
        hourlyRate: 0,
        customTotal: null,
      }
    ]));
  };

  const handleDeleteRow = (index) => {
    setDeviceRows(prev => prev.filter((_, i) => i !== index));
  };

  const filtered = useMemo(() => {
    return bills.filter((b) => {
      // Ищем и по имени, и по почте клиента (раньше при наличии почты имя не учитывалось)
      const userText = [b.user_name, b.user_email, b.user, b.user_id].filter(Boolean).join(' ');
      const titleText = String(b.name || b.title || '');
      const q = String(query || '').toLowerCase();
      const matchesQuery = q ? (userText.toLowerCase().includes(q) || titleText.toLowerCase().includes(q)) : true;
      const matchesStatus = status ? getRawStatus(b.status) === status : true;
      return matchesQuery && matchesStatus;
    });
  }, [query, status, bills]);


  const fetchBills = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      setBills(await fetchAllBills(api, `${BILL_API_BASE_URL}bill`, params));
    } catch (e) {
      console.error('fetchBills error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentRole) return;
    fetchBills();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRole]);

  useRefetchOnHostingChange(() => { fetchBills(); fetchUsers(); });

  // Период меняет выборку на сервере; перевёрнутый период не отправляем
  const periodInvalid = Boolean(dateFrom && dateTo && dateFrom > dateTo);
  useEffect(() => {
    if (!currentRole || periodInvalid) return;
    fetchBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const handleOpenView = (bill) => {
    setViewBill(bill);
    if (Array.isArray(bill.payments)) {
      const mapped = bill.payments.map((p) => ({
        id: p.id,
        method: ({ usdt: t('methodUsdt'), card: t('methodCard'), bank_transfer: t('methodBankTransfer'), crypto: t('methodCrypto'), rub: t('methodRub') })[String(p.method || '').toLowerCase()] || p.method || '-',
        time: formatDateTime(p.date_create || p.created_at),
        amount: p.amount_usdt != null && Number(p.amount_usdt) > 0 ? `${p.amount_usdt} USDT` : (p.amount_rub != null ? formatRubAmount(p.amount_rub, t) : '-'),
        rate: p.course != null ? String(p.course) : '-',
        status: toRuPaymentStatus(p.status || '-'),
        rawStatus: p.status || 'pending'
      }));
      setViewPayments(mapped);
    } else {
      setViewPayments([
        { method: 'USDT', time: '-', amount: '-', rate: '-', status: t('paymentStatusPending'), rawStatus: 'pending' },
      ]);
    }
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewBill(null);
    setViewPayments([]);
  };

  const getPaymentStatusColor = (rawStatus) => {
    const s = String(rawStatus || '').toLowerCase();
    if (s === 'success' || s === 'paid' || s === 'completed') return 'success';
    if (s === 'failed' || s === 'rejected' || s === 'declined') return 'error';
    if (s === 'pending') return 'info';
    return 'default';
  };

  const handleConfirmPaymentRow = async (index) => {
    const payment = viewPayments[index];
    if (!payment?.id) return;
    const url = `${BILL_API_BASE_URL}bill_payment/${payment.id}`;
    try {
      let resp;
      try {
        resp = await api.put(url, { status: 'success' });
      } catch (e) {
        // Сумма подтверждённых платежей меньше суммы счёта: сервер не закрывает счёт без явного согласия
        const data = e?.response?.data;
        if (e?.response?.status !== 409 || data?.code !== 'PAYMENT_AMOUNT_MISMATCH') throw e;
        const ok = window.confirm(t('paymentPartialConfirm', { paid: data.paid_total, amount: data.bill_amount }));
        if (!ok) return;
        resp = await api.put(url, { status: 'success', confirm_partial: true });
      }
      // Статус счёта берём из ответа: при неполной сумме счёт остаётся открытым
      const billStatus = resp?.data?.bill_status || 'paid';
      setViewPayments((prev) => prev.map((p, i) => i === index ? { ...p, status: t('paymentStatusSuccess'), rawStatus: 'success' } : p));
      const applyBill = (bill) => ({ ...bill, status: billStatus, ...(billStatus === 'paid' && !bill.date_payment ? { date_payment: new Date().toISOString() } : {}) });
      setViewBill((prev) => (prev ? applyBill(prev) : prev));
      setBills((prev) => prev.map((bill) => (bill.id === viewBill?.id ? applyBill(bill) : bill)));
    } catch (e) {
      console.error('confirm payment error', e);
      showError(e?.response?.data?.error || t('paymentConfirmError'));
    }
  };

  const handleDeclinePaymentRow = async (index) => {
    try {
      const payment = viewPayments[index];
      if (!payment?.id) return;
      const resp = await api.put(`${BILL_API_BASE_URL}bill_payment/${payment.id}`, { status: 'failed' });
      setViewPayments((prev) => prev.map((p, i) => i === index ? { ...p, status: t('paymentStatusFailed'), rawStatus: 'failed' } : p));
      // Статус счёта — тот, что вернул сервер. Раньше здесь всем счетам списка ставился несуществующий
      // статус failed (условие сравнивало счёт сам с собой, когда открытого счёта не было).
      const billStatus = resp?.data?.bill_status;
      if (billStatus && viewBill?.id) {
        setViewBill((prev) => (prev ? { ...prev, status: billStatus } : prev));
        setBills((prev) => prev.map((b) => (b.id === viewBill.id ? { ...b, status: billStatus } : b)));
      }
    } catch (e) {
      console.error('decline payment error', e);
      showError(e?.response?.data?.error || t('paymentDeclineError'));
    }
  };

  const handleDeleteBill = async (id) => {
    try {
      if (!id) return;
      await api.delete(`${BILL_API_BASE_URL}bill/${id}`);
      await fetchBills();
      handleCloseView();
    } catch (e) {
      console.error('delete bill error', e);
      if (e?.response?.status === 403) {
        showError(t('billDeleteForbidden'));
      } else {
        showError(t('billDeleteError'));
      }
    }
  };

  return (
    <>
      <Menu />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>{t('workHoursTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('workHoursHint')}</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label={t('workHoursMonth')}
              type="month"
              size="small"
              value={workMonth}
              onChange={(e) => setWorkMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleWorkHoursExport} disabled={workExporting || !workMonth}>
              {workExporting ? t('workHoursExporting') : t('workHoursExport')}
            </Button>
          </Box>
        </Paper>
        {(isHoster || currentRole === 'admin' || currentRole === 'super_admin') && <BillingPowerCard defaultMonth={workMonth} />}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Typography variant="h5">{t('issueInvoicesTitle')}</Typography>
            <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => setCreateOpen(true)}>{t('createInvoice')}</Button>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{t('billsFilterTitle')}</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
            <TextField
              label={t('adminLabelUser')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <TextField
              label={t('labelStatus')}
              select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">{t('adminStatusAll')}</MenuItem>
              <MenuItem value="pending">{t('statusPending')}</MenuItem>
              <MenuItem value="paid">{t('statusPaid')}</MenuItem>
              <MenuItem value="overdue">{t('statusOverdue')}</MenuItem>
            </TextField>
            <TextField
              label={t('periodFrom')}
              type="date"
              size="small"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              error={periodInvalid}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t('periodTo')}
              type="date"
              size="small"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              error={periodInvalid}
              helperText={periodInvalid ? t('periodInvalid') : undefined}
              InputLabelProps={{ shrink: true }}
            />
            {(query || status || dateFrom || dateTo) && (
              <Button variant="text" onClick={() => { setQuery(''); setStatus(''); setDateFrom(''); setDateTo(''); }} sx={{ height: 40 }}>
                {t('billsFilterReset')}
              </Button>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 'auto' }}>
              {t('billsFound', { count: filtered.length })}
            </Typography>
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="medium" sx={{ minWidth: 880 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('adminLabelUserColumn')}</TableCell>
                <TableCell>{t('adminLabelEmail')}</TableCell>
                <TableCell>{t('labelTitle')}</TableCell>
                <TableCell>{t('billSourceLabel')}</TableCell>
                <TableCell>{t('labelStatus')}</TableCell>
                <TableCell align="right">{t('labelAmount')}</TableCell>
                <TableCell>{t('adminLabelIssued')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((b) => {
                const sourceLabel = getBillSourceCaption(b, t);
                return (
                  <TableRow key={b.id} hover onClick={() => handleOpenView(b)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      {b.user_id ? (
                        <Button
                          variant="text"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); navigate(`/profile/${b.user_id}`); }}
                        >
                          {b.user_name || '-'}
                        </Button>
                      ) : (b.user_name || '-')}
                    </TableCell>
                    <TableCell>{b.user_email || '-'}</TableCell>
                    <TableCell>{resolveBillName(b, t)}</TableCell>
                    <TableCell>{sourceLabel}</TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(getRawStatus(b.status))} label={toRuStatus(b.status)} />
                    </TableCell>
                    <TableCell align="right">{formatBillAmount(b.amount, b.currency, t)}</TableCell>
                    <TableCell>{formatDateTime(b.date_create || b.created_at || b.issuedAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>
        </Paper>
      </Container>

      <Dialog open={viewOpen} onClose={handleCloseView} maxWidth="lg" fullWidth>
        <DialogTitle>{t('billDetails')}</DialogTitle>
        <DialogContent dividers>
          {viewBill && (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, rowGap: 1.5, columnGap: 2, mb: 2 }}>
                <Typography color="text.secondary">{t('adminLabelUserColumn')}</Typography>
                <Box>
                  {viewBill.user_id ? (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => navigate(`/profile/${viewBill.user_id}`)}
                    >
                      {viewBill.user_name || '-'}
                    </Button>
                  ) : (viewBill.user_name || '-')}
                </Box>

                <Typography color="text.secondary">{t('adminLabelEmail')}</Typography>
                <Typography>{viewBill.user_email || '-'}</Typography>

                <Typography color="text.secondary">{t('labelTitle')}</Typography>
                <Typography>{resolveBillName(viewBill, t)}</Typography>

                <Typography color="text.secondary">{t('billSourceLabel')}</Typography>
                <Typography>{getBillSourceCaption(viewBill, t)}</Typography>

                <Typography color="text.secondary">{t('labelStatus')}</Typography>
                <Box>
                  <Chip size="small" color={statusColor(getRawStatus(viewBill.status))} label={toRuStatus(viewBill.status)} />
                </Box>

                <Typography color="text.secondary">{t('adminLabelIssued')}</Typography>
                <Typography>{formatDateTime(viewBill.date_create || viewBill.created_at || viewBill.issuedAt)}</Typography>

                <Typography color="text.secondary">{t('adminLabelPaid')}</Typography>
                <Typography>{viewBill.date_payment ? formatDateTime(viewBill.date_payment) : '-'}</Typography>

                {/* Убрали курс $ */}

                <Typography color="text.secondary">{t('labelAmount')}</Typography>
                <Typography>{formatBillAmount(viewBill.amount, viewBill.currency, t)}</Typography>
              </Box>

              {/* Таблица услуг */}
              <TableContainer sx={{ mb: 2, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{isCustomBillType(viewBillType) ? t('labelItemName') : t('labelDevice')}</TableCell>
                    <TableCell>{isCustomBillType(viewBillType) ? t('labelItemQuantity') : t('labelHoursCount')}</TableCell>
                    <TableCell align="right">{t('labelAmount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(Array.isArray(viewBill?.content?.items) ? viewBill.content.items : []).map((it, idx) => {
                    const display = getBillItemDisplay(it, viewBillType);
                    const label = isCustomBillType(viewBillType)
                      ? (display.label || '-')
                      : (localizeItemName(display.label, t) || t('labelDevice'));
                    const quantity = display.quantity != null ? display.quantity : '-';
                    return (
                      <TableRow key={idx}>
                        <TableCell>{label}</TableCell>
                        <TableCell>{quantity}</TableCell>
                        <TableCell align="right">{formatBillAmount(display.amount, viewBill?.currency, t)}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={2}><strong>{t('totalSum')}</strong></TableCell>
                    <TableCell align="right">
                      <strong>
                        {formatBillAmount(
                          getBillItemsTotal(viewBill?.content?.items, viewBillType, viewBill?.amount),
                          viewBill?.currency,
                          t
                        )}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              </TableContainer>

              {/* Таблица оплат клиента */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('clientPayment')}</Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('labelMethod')}</TableCell>
                    <TableCell>{t('labelTime')}</TableCell>
                    <TableCell>{t('labelAmount')}</TableCell>
                    <TableCell>{t('labelRate')}</TableCell>
                    <TableCell>{t('labelStatus')}</TableCell>
                    <TableCell align="right">{t('labelActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewPayments.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell>{p.method}</TableCell>
                      <TableCell>{p.time}</TableCell>
                      <TableCell>{p.amount}</TableCell>
                      <TableCell>{p.rate}</TableCell>
                      <TableCell>
                        <Chip size="small" color={getPaymentStatusColor(p.rawStatus)} label={p.status} />
                      </TableCell>
                      <TableCell align="right">
                        {p.rawStatus === 'pending' && (
                          <>
                            <Tooltip title={t('confirmTooltip')}>
                              <IconButton color="success" onClick={() => handleConfirmPaymentRow(i)}>
                                <DoneIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('declineTooltip')}>
                              <IconButton color="error" onClick={() => handleDeclinePaymentRow(i)}>
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {viewBill && (
            <>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={async () => {
                  try {
                    const billId = viewBill.id || viewBill.uuid;
                    const res = await api.get(`${BILL_API_BASE_URL}bill/${billId}/pdf`, { responseType: 'blob' });
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `bill_${String(billId).substring(0, 8)}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    if (e?.response?.status === 412) {
                      showError(t('requisitesMissing'));
                    } else {
                      console.error('Payments: ошибка скачивания PDF', e);
                    }
                  }
                }}
              >
                {t('downloadPdf')}
              </Button>
              {(() => {
                const bt = String(viewBill.type || '').toLowerCase();
                const hosterCanDelete = ['custom', 'service'].includes(bt);
                if (isHoster && !hosterCanDelete) return null;
                return (
                  <Button color="error" onClick={() => handleDeleteBill(viewBill.id || viewBill.uuid)}>{t('deleteInvoice')}</Button>
                );
              })()}
            </>
          )}
          <Button onClick={handleCloseView} variant="contained">{t('labelClose')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{t('createInvoiceTitle')}</DialogTitle>
        <DialogContent dividers>
          {(
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Button
                variant={billMode === 'kwh' ? 'contained' : 'outlined'}
                onClick={() => { setBillMode('kwh'); setInvoiceTitle(t('kwhInvoiceFor', { month: monthNames[selectedMonth] })); }}
                size="small"
              >
                {t('kwhInvoice')}
              </Button>
              {!isHoster && (
              <Button
                variant={billMode === 'monitoring' ? 'contained' : 'outlined'}
                onClick={() => setBillMode('monitoring')}
                size="small"
              >
                {t('monitoringInvoice')}
              </Button>
              )}
              <Button
                variant={billMode === 'custom' ? 'contained' : 'outlined'}
                onClick={() => setBillMode('custom')}
                size="small"
              >
                {t('customInvoice')}
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: billMode !== 'custom' ? { xs: '1fr', sm: '1fr 1fr 1fr' } : '1fr', gap: 2, mb: 2 }}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => {
                if (!option) return '';
                return option.email ? `${option.name || option.email} (${option.email})` : (option.name || option.email || '');
              }}
              value={users.find(u => u.id === selectedUser) || null}
              onChange={(_, newValue) => {
                const val = newValue?.id || '';
                setSelectedUser(val);
                void fetchClientRequisites(val);
                if (val) {
                  void fetchPaymentsPreview(val, selectedYear, selectedMonth);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('userField')}
                  size="small"
                  placeholder={t('userFieldPlaceholder')}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Box>
                      <Typography variant="body2">{option.name || option.email}</Typography>
                      {option.email && option.name && (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase().trim();
                if (!searchTerm) return options;

                return options.filter((option) => {
                  const name = (option.name || '').toLowerCase();
                  const email = (option.email || '').toLowerCase();
                  return name.includes(searchTerm) || email.includes(searchTerm);
                });
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={t('noUsersFound')}
            />
            {billMode !== 'custom' && (
              <TextField
                label={t('monthField')}
                select
                value={selectedMonth}
                onChange={(e) => { const m = Number(e.target.value); const key = billMode === 'kwh' ? 'kwhInvoiceFor' : 'equipmentInvoiceFor'; const prevDefault = t(key, { month: monthNames[selectedMonth] }); if (!invoiceTitle || invoiceTitle === prevDefault) { setInvoiceTitle(t(key, { month: monthNames[m] })); } setSelectedMonth(m); handleMonthOrYearChange(selectedYear, m); }}
                size="small"
              >
                {monthNames.map((m, idx) => (
                  <MenuItem key={idx} value={idx}>{m} | {calcHoursInMonth(selectedYear, idx)}</MenuItem>
                ))}
              </TextField>
            )}
            {billMode !== 'custom' && (
              <TextField
                label={t('yearField')}
                type="number"
                value={selectedYear}
                onChange={(e) => { const y = Number(e.target.value); setSelectedYear(y); handleMonthOrYearChange(y, selectedMonth); }}
                size="small"
              />
            )}
          </Box>
          {selectedUser && (
            <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
              <Autocomplete
                sx={{ flex: 1 }}
                size="small"
                options={clientReqOptions}
                loading={clientReqLoading}
                getOptionLabel={(opt) => formatClientReqOption(opt)}
                value={clientReqOptions.find((r) => r.id === selectedClientReqId) || null}
                onChange={(_, newVal) => setSelectedClientReqId(newVal?.id || '')}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={t('billClientRequisitesEmpty')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('billClientRequisites')}
                    placeholder={t('billClientRequisitesPlaceholder')}
                    size="small"
                    helperText={clientReqOptions.length === 0 && !clientReqLoading ? t('billClientRequisitesHint') : ''}
                    error={clientReqOptions.length === 0 && !clientReqLoading}
                  />
                )}
              />
            </Box>
          )}
          {selectedUser && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2 }}>
              <TextField
                fullWidth
                label={t('titleField')}
                value={invoiceTitle}
                onChange={(e) => setInvoiceTitle(e.target.value)}
                size="small"
              />
              {billMode === 'monitoring' && (
                <Tooltip title={t('addRow')}>
                  <IconButton color="primary" onClick={handleAddRow}>
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
              {billMode === 'custom' && (
                <Tooltip title={t('addPosition')}>
                  <IconButton color="primary" onClick={() => setCustomRows(prev => [...prev, { name: '', amount: 0 }])}>
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {selectedUser && billMode === 'custom' && !customItemsValid && (
            <Alert severity="info" sx={{ mb: 1 }}>{t('billItemRequired')}</Alert>
          )}

          {selectedUser && billMode === 'custom' && (
            <Table size="small" sx={{ '& .MuiTableCell-root': { p: '5px' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('customItemName')}</TableCell>
                  <TableCell width={150}>{t('labelAmount')}</TableCell>
                  <TableCell align="center" width={56}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customRows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <TextField
                        value={r.name}
                        onChange={(e) => setCustomRows(prev => prev.map((row, idx) => idx === i ? { ...row, name: e.target.value } : row))}
                        size="small"
                        fullWidth
                        placeholder={t('customItemPlaceholder')}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={r.amount}
                        onChange={(e) => setCustomRows(prev => prev.map((row, idx) => idx === i ? { ...row, amount: Number(e.target.value) || 0 } : row))}
                        size="small"
                        inputProps={{ min: 0 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">р</InputAdornment> }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="error" onClick={() => setCustomRows(prev => prev.filter((_, idx) => idx !== i))}>
                        <CloseIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {selectedUser && billMode === 'kwh' && (
            <Box>
              {kwhLoading && <Typography variant="body2" color="text.secondary">{t('kwhLoading')}</Typography>}
              {!kwhLoading && kwhError && <Typography variant="body2" color="error">{kwhError}</Typography>}
              {!kwhLoading && !kwhError && kwhPreview && kwhPreview.items?.length === 0 && (
                <Typography variant="body2" color="text.secondary">{t('kwhNoHours')}</Typography>
              )}
              {!kwhLoading && kwhPreview?.items?.length > 0 && (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { p: '5px' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('deviceColumn')}</TableCell>
                        <TableCell>{t('kwhWorkerColumn')}</TableCell>
                        <TableCell align="right">{t('kwhHoursColumn')}</TableCell>
                        <TableCell align="right">{t('kwhPowerColumn')}</TableCell>
                        <TableCell align="right">{t('kwhColumn')}</TableCell>
                        <TableCell align="right">{t('kwhPriceColumn')}</TableCell>
                        <TableCell align="right">{t('kwhSumColumn')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {kwhPreview.items.map((it) => (
                        <TableRow key={it.device_id}>
                          <TableCell>{it.device}{it.sn ? <Typography variant="caption" color="text.secondary" display="block">{it.sn}</Typography> : null}</TableCell>
                          <TableCell>{it.worker || '—'}</TableCell>
                          <TableCell align="right">{Number(it.hours).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}</TableCell>
                          <TableCell align="right">{it.power_w != null ? Number(it.power_w).toLocaleString('ru-RU') : '—'}</TableCell>
                          <TableCell align="right">{Number(it.kwh).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}</TableCell>
                          <TableCell align="right">{it.kwh_price != null ? Number(it.kwh_price).toLocaleString('ru-RU', { minimumFractionDigits: 2 }) : '—'}</TableCell>
                          <TableCell align="right">{Number(it.sum).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t('kwhHint')}</Typography>
            </Box>
          )}

          {selectedUser && billMode === 'monitoring' && (
          <Table size="small" sx={{ '& .MuiTableCell-root': { p: '5px' } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('deviceColumn')}</TableCell>
                <TableCell>{t('snColumn')}</TableCell>
                <TableCell>{t('activeHoursColumn')}</TableCell>
                <TableCell>{t('monthlyTariffColumn')}</TableCell>
                <TableCell>{t('hourlyRateColumn')}</TableCell>
                <TableCell align="right">{t('resultColumn')}</TableCell>
                <TableCell align="center" width={56}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deviceRows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell width={160}>
                    <TextField
                      value={r.device}
                      onChange={(e) => handleChangeDevice(i, e.target.value)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={r.sn || ''}
                      onChange={(e) => handleChangeSN(i, e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell width={80}>
                    <TextField
                      type="number"
                      value={r.activeHours}
                      onChange={(e) => handleChangeActiveHours(i, e.target.value)}
                      size="small"
                      inputProps={{ min: 0 }}
                    />
                  </TableCell>
                  <TableCell width={100}>
                    <TextField
                      type="text"
                      value={String(Math.floor(Number(r.monthlyTariff) || 0))}
                      onChange={(e) => handleChangeMonthlyTariff(i, e.target.value)}
                      size="small"
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </TableCell>
                  <TableCell width={90}>
                    <TextField
                      type="text"
                      value={Number(r.hourlyRate).toFixed(2)}
                      onChange={(e) => handleChangeHourlyRate(i, e.target.value)}
                      size="small"
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </TableCell>
                  <TableCell align="right" width={100}>
                    <TextField
                      type="text"
                      value={String(rowTotal(r))}
                      onChange={(e) => handleChangeRowTotal(i, e.target.value)}
                      size="small"
                      inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">р</InputAdornment>,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" width={56}>
                    <IconButton color="error" aria-label={t('deleteInvoice')} onClick={() => handleDeleteRow(i)}>
                      <CloseIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}

          {selectedUser && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Typography variant="subtitle1"><strong>{t('grandTotal')} {billMode === 'custom' ? customRows.reduce((s, r) => s + (Number(r.amount) || 0), 0) : billMode === 'kwh' ? (kwhPreview?.amount ?? 0).toLocaleString('ru-RU') : String(Math.ceil(grandTotal))} р</strong></Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('cancelButton')}</Button>
          <Button
            variant="contained"
            disabled={!selectedUser || (billMode === 'custom' && !customItemsValid) || (billMode === 'kwh' && !(kwhPreview?.amount > 0))}
            onClick={async () => {
            try {
              let payload;
              if (billMode === 'custom') {
                const validItems = customRows
                  .filter((r) => r && String(r.name || '').trim().length > 0 && Number(r.amount) > 0)
                  .map((r) => ({ name: String(r.name).trim(), amount: Math.ceil(Number(r.amount)) }));
                const customTotal = validItems.reduce((s, r) => s + r.amount, 0);
                payload = {
                  user_id: selectedUser,
                  name: invoiceTitle || t('labelInvoice'),
                  content: { items: validItems },
                  amount: customTotal,
                  status: 'pending',
                  type: 'custom',
                };
              } else if (billMode === 'kwh') {
                // Позиции и сумму сервер пересчитает сам из посуточного расчёта; amount — то, что видит хостер
                payload = {
                  user_id: selectedUser,
                  name: invoiceTitle || t('kwhInvoiceFor', { month: monthNames[selectedMonth] }),
                  content: { method: 'kwh', year: selectedYear, month: selectedMonth + 1 },
                  amount: kwhPreview.amount,
                  status: 'pending',
                  type: 'monitoring',
                  // та же площадка, по которой показан расчёт
                  hosting_id: kwhPreview.hosting_id,
                };
              } else {
                payload = {
                  user_id: selectedUser,
                  name: invoiceTitle || t('labelInvoice'),
                  content: {
                    items: deviceRows.map((row) => ({
                      ...row,
                      worker: row.worker || ''
                    })),
                    month: selectedMonth,
                    year: selectedYear,
                  },
                  amount: Math.ceil(grandTotal),
                  status: 'pending',
                };
              }
              if (selectedClientReqId) payload.client_requisites_id = selectedClientReqId;
              await api.post(`${BILL_API_BASE_URL}bill`, payload);
              setCreateOpen(false);
              setCustomRows([{ name: '', amount: 0 }]);
              fetchBills();
            } catch (e) {
              const code = e?.response?.data?.code;
              if (code === 'CLIENT_REQUISITES_REQUIRED') {
                showError(t('billRequisitesRequired'));
              } else if (code === 'HOSTING_REQUISITES_REQUIRED') {
                showError(t('billHostingRequisitesRequired'));
              } else if (code === 'ITEMS_REQUIRED' || code === 'ITEM_NAME_REQUIRED') {
                showError(t('billItemRequired'));
              } else if (code === 'ITEM_AMOUNT_INVALID') {
                showError(t('billItemAmountInvalid'));
              } else if (code === 'NO_WORKED_HOURS') {
                showError(t('kwhNoHours'));
              } else if (code === 'KWH_BILL_EXISTS') {
                showError(t('kwhBillExists'));
              } else {
                showError(t('billCreateError'));
              }
              console.error('create bill error', e);
            }
          }}>{t('submitInvoice')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Payments;


